'use client';
import { useCallback, useRef } from 'react';

/**
 * A hook that returns a debounced version of the provided function.
 * The debounced function will only execute after the specified delay has passed
 * without any new calls to the function.
 *
 * @param callback - The function to debounce
 * @param delay - The delay in milliseconds
 * @returns A debounced version of the callback function
 */
export function useDebounceFunctions<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
): (...args: Parameters<T>) => void {
  // Store the timeout ID in a ref to persist between renders
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Wrap the debounced function in useCallback to prevent unnecessary re-renders
  return useCallback(
    (...args: Parameters<T>) => {
      // Clear any existing timeout to prevent multiple executions
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set a new timeout to execute the callback after the delay
      timeoutRef.current = setTimeout(() => {
        // Execute the callback with the latest arguments
        callback(...args);
      }, delay);
    },
    [callback, delay],
  );
}
